import { useEffect, useRef, useState } from "react";
import { FileUp } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/use-toast";
import { extractErrorMessage } from "@/api/client";
import { useImportTopsolidDoZakazky } from "@/api/hooks/useImport";

export interface TopSolidDoZakazkyDialogProps {
  zakazkaId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Zavolá sa po úspešnom importe s ID novej výrobnej varianty. */
  onImported?: (variantaId: string) => void;
  /** Zákazka už má výrobnú variantu — import pridá ďalšiu etapu (bez AA). */
  maVyrobnu?: boolean;
  /** Malá zákazka bez AA (KOLO 200) — číslo Z ostane, AA sa nepýta. */
  cisloZ?: string | null;
}

/**
 * Import TopSolid CSV do existujúcej zákazky — vytvorí VÝROBNÚ variantu
 * (realita výroby na kalkuláciu). Cenové varianty a aktívna varianta
 * sa nemenia.
 */
export function TopSolidDoZakazkyDialog({
  zakazkaId,
  open,
  onOpenChange,
  onImported,
  maVyrobnu = false,
  cisloZ = null,
}: TopSolidDoZakazkyDialogProps): JSX.Element {
  const pytaAa = !maVyrobnu && !cisloZ;
  const importMutation = useImportTopsolidDoZakazky();
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [vyrobneCislo, setVyrobneCislo] = useState("");

  useEffect(() => {
    if (!open) {
      setFile(null);
      setVyrobneCislo("");
      if (fileRef.current) fileRef.current.value = "";
    }
  }, [open]);

  async function handleImport(): Promise<void> {
    if (!file) return;
    try {
      const { data } = await importMutation.mutateAsync({
        zakazkaId,
        file,
        vyrobne_cislo: pytaAa ? vyrobneCislo.trim() || undefined : undefined,
      });
      const s = data.import_summary;
      const unmatched = s.unmatched_list ?? [];
      toast({
        title: maVyrobnu
          ? "Ďalšia etapa výroby importovaná"
          : "Výrobná varianta importovaná",
        description:
          `${s.zostavy_created} zostáv, ${s.dielce_created} dielcov` +
          (s.materials_unmatched > 0
            ? ` · ${s.materials_unmatched} nenamapovaných materiálov`
            : ""),
      });
      if (unmatched.length > 0) {
        toast({
          variant: "destructive",
          title: "Nenamapované materiály",
          description:
            unmatched.slice(0, 5).join(", ") +
            (unmatched.length > 5 ? ` … (+${unmatched.length - 5})` : ""),
        });
      }
      onOpenChange(false);
      if (data.varianta_id) onImported?.(data.varianta_id);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Import zlyhal",
        description: extractErrorMessage(err),
      });
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Import TopSolid — výrobná varianta</DialogTitle>
          <DialogDescription>
            {maVyrobnu
              ? "CSV z TopSolidu sa naimportuje ako ďalšia etapa výroby (Výrobná 2…). Cenové varianty aj AA číslo zákazky ostávajú nezmenené."
              : "CSV z TopSolidu sa naimportuje do tejto zákazky ako výrobná varianta — najaktuálnejšia realita výroby na kalkuláciu. Cenové varianty (A, B, C…) ostávajú nezmenené."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <input
            ref={fileRef}
            type="file"
            accept=".csv"
            aria-label="TopSolid CSV súbor"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="block w-full text-sm file:mr-3 file:rounded-md file:border-0 file:bg-primary file:px-3 file:py-1.5 file:text-sm file:text-primary-foreground hover:file:bg-primary/90"
          />
          {file ? (
            <p className="text-xs text-muted-foreground">
              {file.name} · {(file.size / 1024).toFixed(0)} kB
            </p>
          ) : null}
        </div>

        {pytaAa ? (
        <div className="space-y-2">
          <Label htmlFor="ts-vyrobne-cislo">Výrobné číslo (AA)</Label>
          <Input
            id="ts-vyrobne-cislo"
            placeholder="napr. AA6025"
            value={vyrobneCislo}
            onChange={(e) => setVyrobneCislo(e.target.value)}
          />
          <p className="text-[11px] text-muted-foreground">
            Zákazka prejde do výroby. AA sa stane hlavným číslom, číslo
            výpočtu (V) ostane sekundárne. Prázdne = číslo sa nezmení.
          </p>
        </div>
        ) : null}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={importMutation.isPending}
          >
            Zrušiť
          </Button>
          <Button
            onClick={() => void handleImport()}
            disabled={!file || importMutation.isPending}
          >
            <FileUp className="h-4 w-4" />
            {importMutation.isPending ? "Importuje sa…" : "Importovať"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
